import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http, setStoredToken } from "@/lib/api";
import { identifyClarity, resetClarity } from "@/lib/clarity";

const AuthContext = createContext(null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Persist the JWT returned by /auth/register|login|google/session so the
// app can survive the proxy-stripped cookie scenario via Authorization
// header. We strip it from the user object before storing in state.
function persistAndStripToken(data) {
  if (data && typeof data === "object" && data.session_token) {
    setStoredToken(data.session_token);
    const { session_token, ...user } = data;
    return user;
  }
  return data;
}

export function AuthProvider({ children }) {
  // null = checking, false = not authed, object = authed user
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
      // Link the Clarity session to this user — also runs on page refresh,
      // so reloaded tabs continue to attribute heatmap data to the user.
      identifyClarity(data);
      return data;
    } catch (_e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // Skip the /me check while returning from Google OAuth — AuthCallback
    // will exchange the session_id and set the cookie first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await http.post("/auth/login", { email, password });
    const u = persistAndStripToken(data);
    setUser(u);
    identifyClarity(u);
    return u;
  };

  const register = async (email, password, name) => {
    const { data } = await http.post("/auth/register", { email, password, name });
    const u = persistAndStripToken(data);
    setUser(u);
    identifyClarity(u);
    return u;
  };

  const exchangeGoogleSession = async (sessionId) => {
    const { data } = await http.post("/auth/google/session", { session_id: sessionId });
    const u = persistAndStripToken(data);
    setUser(u);
    identifyClarity(u);
    return u;
  };

  const logout = async () => {
    try { await http.post("/auth/logout"); } catch (_e) {}
    setStoredToken(null);
    setUser(false);
    // Detach the Clarity session from the previous user so the next visitor
    // (or sign-in) starts fresh.
    resetClarity();
  };

  return (
    <AuthContext.Provider
      value={{ user, login, register, logout, exchangeGoogleSession, refresh: fetchMe }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
